import { Box, Popover } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { Analytics } from '@/shared/lib';
import type { SortingItem } from '@/shared/types';
import { SortOptions, SortTypes } from '@/shared/types';
import { getColumnSorting, HeaderCell, SortButton } from '@/shared/ui';
import { FilterIconButton } from '../FilterIconButton/FilterIconButton';
import { useTableHeaderPropsContext } from '../TableHeaderPropsContext/TableHeaderPropsContext';
import { AddHighlight } from './AddHighlight';

enum ActionType {
  addFilter = 'addFilter',
  clearFilter = 'clearFilter',
  addHighlight = 'addHighlight',
  clearHighlight = 'clearHighlight',
  sort = 'sort',
  sortAscending = 'sortAscending',
  sortDescending = 'sortDescending',
  delete = 'delete',
}

interface Props {
  field: string;
  name: string;
  description?: string;
}

export function MetricTableHeader({ field, name, description }: Props) {
  const menuRef = useRef<HTMLButtonElement>(null);
  const [activeAction, setActiveAction] = useState<ActionType | null>(null);
  const {
    metricsColoring,
    filters,
    filtersMap,
    filterableIds,
    renderMetricFilterPanel,
    sorting,
    updateSorting,
    updateFilters,
    updateMetricsColoring,
    onMetricDelete,
  } = useTableHeaderPropsContext();
  const sortingItem = getColumnSorting(sorting, field);
  const menuOptions = useMemo(() => {
    const isFilterAllowed = filterableIds?.has(field);
    const filter = filtersMap?.[field];
    const metricColoring = metricsColoring.find(
      (coloring) => coloring.metric === field,
    );

    return [
      ...(isFilterAllowed
        ? [
            {
              label: filter ? 'Edit Filter' : 'Add Filter',
              value: ActionType.addFilter,
            },
          ]
        : []),
      ...(filter
        ? [
            {
              label: 'Clear Filter',
              value: ActionType.clearFilter,
            },
          ]
        : []),
      {
        label: metricColoring ? 'Edit Highlight' : 'Add Highlight',
        value: ActionType.addHighlight,
      },
      ...(metricColoring
        ? [
            {
              label: 'Clear Highlight',
              value: ActionType.clearHighlight,
            },
          ]
        : []),
      {
        label: 'Sort',
        value: ActionType.sort,
        selected: sortingItem?.type === SortTypes.SIMPLE,
        divider: true,
        nestedOptions: [
          {
            label: 'Ascending',
            value: ActionType.sortAscending,
            selected:
              sortingItem?.type === SortTypes.SIMPLE &&
              sortingItem?.value === SortOptions.ASC,
          },
          {
            label: 'Descending',
            value: ActionType.sortDescending,
            selected:
              sortingItem?.type === SortTypes.SIMPLE &&
              sortingItem?.value === SortOptions.DESC,
          },
        ],
      },
      {
        label: 'Delete',
        value: ActionType.delete,
      },
    ];
  }, [sortingItem, filtersMap, filterableIds, field, metricsColoring]);

  const handleSimpleSortingUpdate =
    (name: string) => (newSortOption?: SortOptions) => {
      const sortingItem: SortingItem | undefined = newSortOption
        ? {
            type: SortTypes.SIMPLE,
            name,
            value: newSortOption,
          }
        : undefined;

      updateSorting(name, sortingItem);
      Analytics.sendFeatureUsage('data_table', 'simple_sorting');
    };
  const handleActionClose = () => {
    setActiveAction(null);
  };
  const handleAddFilter = () => {
    setActiveAction(ActionType.addFilter);
  };
  const handleClearFilter = () => {
    updateFilters(filters.filter((filter) => filter.id !== field));
  };
  const handleClearHighlight = () => {
    updateMetricsColoring(
      metricsColoring.filter((coloring) => coloring.metric !== field),
    );
  };
  const handleMenuOptionClick = (optionValue: string) => {
    if (optionValue === ActionType.addFilter) {
      handleAddFilter();
    } else if (optionValue === ActionType.clearFilter) {
      handleClearFilter();
    } else if (optionValue === ActionType.addHighlight) {
      setActiveAction(ActionType.addHighlight);
    } else if (optionValue === ActionType.clearHighlight) {
      handleClearHighlight();
    } else if (optionValue === ActionType.sortAscending) {
      handleSimpleSortingUpdate(field)(SortOptions.ASC);
    } else if (optionValue === ActionType.sortDescending) {
      handleSimpleSortingUpdate(field)(SortOptions.DESC);
    } else if (optionValue === ActionType.delete) {
      onMetricDelete(field);
    }
  };

  return (
    <>
      <HeaderCell
        field={field}
        title={name}
        ref={menuRef}
        description={description}
        quickActions={(hovered) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {(hovered || !!sortingItem) && (
                <SortButton
                  order={sortingItem?.index}
                  value={sortingItem?.value}
                  onChange={handleSimpleSortingUpdate(field)}
                />
              )}
              {!!filtersMap?.[field] && (
                <FilterIconButton onClick={handleAddFilter} />
              )}
            </Box>
          );
        }}
        menuOptions={menuOptions}
        onMenuOptionClick={handleMenuOptionClick}
      />
      <Popover
        open={activeAction !== null}
        anchorEl={menuRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          },
        }}
        onClose={handleActionClose}
      >
        {activeAction === ActionType.addFilter &&
          renderMetricFilterPanel?.(field, handleActionClose)}
        {activeAction === ActionType.addHighlight && (
          <AddHighlight id={field} />
        )}
      </Popover>
    </>
  );
}
